import React, { FC, useCallback } from 'react';

import { useIntl } from 'react-intl';

import ActionButton, { ActionButtonSizes } from 'components/action-button';
import ItemActions from 'components/item-actions';
import { ListItemGroup } from 'components/list-item-group';
import useFoodware from 'hooks/use-foodware';
import { useReorderEntries } from 'pages/menu/hooks/use-reorder-entries';
import { RecentOrderCard } from 'pages/menu/recent-items-page/recent-order-card-list/recent-order-card-list-item';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { useReorder } from 'state/order/hooks/use-reorder';

import { IFavoriteCardProps } from './types';

/**
 *
 * FavoriteCard purpose
 *
 */

export const FavoriteCard: FC<React.PropsWithChildren<IFavoriteCardProps>> = ({
  id,
  entries,
  name,
  onEditFavorite,
  onRemoveFavorite,
}) => {
  const favoriteOrder = {
    cart: {
      cartEntries: entries,
      reorderCartEntries: entries,
      serviceMode: null,
    },
  };

  const { formatMessage } = useIntl();

  const { handleReorderClick } = useReorder(favoriteOrder);
  const { reorderEntriesMap, checkingAvailability, hasSomeAvailableEntry } = useReorderEntries(
    favoriteOrder?.cart.cartEntries
  );

  const handleReorder = useCallback(() => {
    for (const entry of favoriteOrder.cart.cartEntries) {
      logEvent(CustomEventNames.ADD_FAVORITE_TO_CART, EventTypes.UserContent, {
        'Product Name': entry?.name,
        'User Location': 'Favorites Tab',
      });
    }
    handleReorderClick();
  }, [favoriteOrder.cart.cartEntries, handleReorderClick]);

  const { isFoodware } = useFoodware();

  const filterFavoriteEntries = () => {
    return entries.reduce((acc: React.ReactNode[], entry) => {
      if (!entry.isExtra && !entry.isDonation && !isFoodware(entry)) {
        acc.push(
          <RecentOrderCard
            key={entry?.lineId}
            cartEntry={entry}
            mappedEntry={reorderEntriesMap[entry.lineId] ?? {}}
            checkingAvailability={checkingAvailability}
            isOnFavoriteSection
          />
        );
      }
      return acc;
    }, []);
  };

  if (!entries.length) {
    return null;
  }
  return (
    <ListItemGroup
      header={name || ''}
      headerAction={
        <ActionButton
          testID="reorder-button"
          onPress={handleReorder}
          disabled={!hasSomeAvailableEntry}
          size={ActionButtonSizes.SMALL}
        >
          {formatMessage({ id: 'orderNow' })}
        </ActionButton>
      }
      items={filterFavoriteEntries()}
      footer={
        <ItemActions
          onEdit={() => onEditFavorite(id ?? '')}
          onRemove={() => onRemoveFavorite(id ?? '')}
        />
      }
      key={id}
    />
  );
};
